import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ClipboardCopy, CopyPlus, Download, Save } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { AppShell } from "@/components/AppShell";
import { FeatureGate } from "@/components/UpgradeGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ConfigPane } from "@/components/visuals/ConfigPane";
import { DataPane } from "@/components/visuals/DataPane";
import { VisualCanvas } from "@/components/visuals/VisualCanvas";
import { autoMap } from "@/lib/visuals/map";
import { planErrorMessage } from "@/lib/plan-errors";
import { copyPng, exportPng, exportSvg, preloadExporter } from "@/lib/visuals/export";
import {
  KIND_GROUPS,
  normaliseGrid,
  withDefaults,
  type Grid,
  type VisualConfig,
  type VisualKind,
} from "@/lib/visuals/types";

export const Route = createFileRoute("/_authenticated/visuals/$id")({
  head: () => ({
    meta: [
      { title: "Edit visual — MyPriorities" },
      {
        name: "description",
        content:
          "Map your columns, style the visual and export it straight into a deck or document.",
      },
      { property: "og:title", content: "Edit visual — MyPriorities" },
      {
        property: "og:description",
        content: "Shape a chart, roadmap or framework from your own data and export it.",
      },
    ],
  }),
  component: EditorRoute,
});

function EditorRoute() {
  return (
    <FeatureGate requires="pro" title="Visuals studio">
      <Editor />
    </FeatureGate>
  );
}

function Editor() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["visual", id],
    queryFn: async () => {
      const { data, error } = await supabase.from("visuals").select("*").eq("id", id).single();
      if (error) throw error;
      return data;
    },
  });

  const [kind, setKind] = useState<VisualKind>("bar");
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [sourceNote, setSourceNote] = useState("");
  const [grid, setGrid] = useState<Grid>({ headers: [], rows: [] });
  const [config, setConfig] = useState<VisualConfig>(withDefaults(null));
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!data || loadedId === id) return;
    setKind(data.kind as VisualKind);
    setTitle(data.title);
    setSubtitle(data.subtitle ?? "");
    setSourceNote(data.source_note ?? "");
    setGrid(normaliseGrid(data.data));
    setConfig(withDefaults(data.config));
    setDirty(false);
    setLoadedId(id);
  }, [data, loadedId, id]);

  const save = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("visuals")
        .update({
          kind,
          title: title.trim() || "Untitled visual",
          subtitle: subtitle.trim() || null,
          source_note: sourceNote.trim() || null,
          config: config as never,
          data: grid as never,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: ["visuals"] });
      void queryClient.invalidateQueries({ queryKey: ["visual", id] });
      toast.success("Saved.");
    },
    onError: (e: Error) => toast.error(planErrorMessage(e, "Couldn't save this visual.")),
  });

  function updateGrid(next: Grid) {
    setGrid(next);
    setConfig((c) => autoMap(next, kind, c));
    setDirty(true);
  }

  function updateKind(next: VisualKind) {
    setKind(next);
    setConfig((c) => autoMap(grid, next, c));
    setDirty(true);
  }

  function updateConfig(next: VisualConfig) {
    setConfig(next);
    setDirty(true);
  }

  const exportAction = useMutation({
    mutationFn: async (mode: "png" | "svg") => {
      const node = canvasRef.current;
      if (!node) throw new Error("Nothing to export yet.");
      if (mode === "png") await exportPng(node, title, config.exportScale);
      else await exportSvg(node, title);
      return mode;
    },
    onSuccess: () => toast.success("Downloaded."),
    onError: (e: Error) => toast.error(e.message || "Export failed."),
  });

  const [copying, setCopying] = useState(false);

  useEffect(() => {
    void preloadExporter();
  }, []);

  /** Called straight from the click so Safari keeps clipboard permission alive. */
  function handleCopy() {
    const node = canvasRef.current;
    if (!node) {
      toast.error("Nothing to export yet.");
      return;
    }
    setCopying(true);
    copyPng(node, config.exportScale)
      .then(() => toast.success("Copied — paste it into your deck."))
      .catch(async (e: Error) => {
        try {
          await exportPng(node, title, config.exportScale);
          toast.success("Clipboard blocked — downloaded the PNG instead.");
        } catch {
          toast.error(e.message || "Couldn't copy the image.");
        }
      })
      .finally(() => setCopying(false));
  }

  const [reuseKind, setReuseKind] = useState<VisualKind>("bar");

  /** Spin the current data off into a brand new visual, leaving this one untouched. */
  const reuse = useMutation({
    mutationFn: async () => {
      const { data: auth } = await supabase.auth.getUser();
      const user = auth.user;
      if (!user) throw new Error("Please sign in again.");
      const label =
        KIND_GROUPS.flatMap((g) => g.kinds).find((k) => k.kind === reuseKind)?.label ?? "visual";
      const { data: created, error } = await supabase
        .from("visuals")
        .insert({
          user_id: user.id,
          kind: reuseKind,
          title: `${title || "Untitled visual"} — ${label}`,
          subtitle: subtitle.trim() || null,
          source_note: sourceNote.trim() || null,
          config: autoMap(grid, reuseKind, withDefaults(config)) as never,
          data: grid as never,
        })
        .select("id")
        .single();
      if (error) throw error;
      return created.id as string;
    },
    onSuccess: (newId) => {
      void queryClient.invalidateQueries({ queryKey: ["visuals"] });
      toast.success("New visual created from this data.");
      void navigate({ to: "/visuals/$id", params: { id: newId } });
    },
    onError: (e: Error) => toast.error(planErrorMessage(e, "Couldn't create that visual.")),
  });

  const kindHint = useMemo(
    () => KIND_GROUPS.flatMap((g) => g.kinds).find((k) => k.kind === kind)?.hint ?? "",
    [kind],
  );

  if (isLoading) {
    return (
      <AppShell title="Visuals studio">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </AppShell>
    );
  }

  if (error || !data) {
    return (
      <AppShell title="Visuals studio">
        <p className="text-sm text-muted-foreground">
          That visual couldn't be found.{" "}
          <Link to="/visuals" className="underline">
            Back to the gallery
          </Link>
          .
        </p>
      </AppShell>
    );
  }

  return (
    <AppShell
      title={title || "Untitled visual"}
      subtitle={kindHint}
      action={
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="ghost" size="sm">
            <Link to="/visuals">
              <ArrowLeft className="size-4" /> Gallery
            </Link>
          </Button>
          <Button size="sm" variant="outline" onClick={handleCopy} disabled={copying}>
            <ClipboardCopy className="size-4" /> {copying ? "Copying…" : "Copy image"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => exportAction.mutate("png")}
            disabled={exportAction.isPending}
          >
            <Download className="size-4" /> PNG
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => exportAction.mutate("svg")}
            disabled={exportAction.isPending}
          >
            <Download className="size-4" /> SVG
          </Button>
          <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending || !dirty}>
            <Save className="size-4" /> {save.isPending ? "Saving…" : dirty ? "Save" : "Saved"}
          </Button>
        </div>
      }
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="space-y-4">
          <VisualCanvas
            ref={canvasRef}
            kind={kind}
            grid={grid}
            config={config}
            title={title}
            subtitle={subtitle}
            sourceNote={sourceNote}
          />
          <div className="panel p-4">
            <h2 className="label-caps">Data</h2>
            <div className="mt-3">
              <DataPane kind={kind} grid={grid} onChange={updateGrid} />
            </div>
          </div>
        </div>

        <aside className="space-y-4">
          <div className="panel space-y-3 p-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Visual type</Label>
              <Select value={kind} onValueChange={(v) => updateKind(v as VisualKind)}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KIND_GROUPS.map((group) => (
                    <SelectGroup key={group.group}>
                      <SelectLabel>{group.group}</SelectLabel>
                      {group.kinds.map((k) => (
                        <SelectItem key={k.kind} value={k.kind}>
                          {k.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Title</Label>
              <Input
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setDirty(true);
                }}
                maxLength={120}
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Subtitle</Label>
              <Input
                value={subtitle}
                onChange={(e) => {
                  setSubtitle(e.target.value);
                  setDirty(true);
                }}
                maxLength={200}
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Source note</Label>
              <Input
                value={sourceNote}
                onChange={(e) => {
                  setSourceNote(e.target.value);
                  setDirty(true);
                }}
                maxLength={200}
                placeholder="Source: FY26 plan, Aug 2026"
                className="h-9"
              />
            </div>
          </div>

          <div className="panel space-y-3 p-4">
            <div>
              <h2 className="label-caps">Reuse this data</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Keep this visual as it is and build another one from the same table.
              </p>
            </div>
            <Select value={reuseKind} onValueChange={(v) => setReuseKind(v as VisualKind)}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {KIND_GROUPS.map((group) => (
                  <SelectGroup key={group.group}>
                    <SelectLabel>{group.group}</SelectLabel>
                    {group.kinds.map((k) => (
                      <SelectItem key={k.kind} value={k.kind}>
                        {k.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="outline"
              className="w-full"
              onClick={() => reuse.mutate()}
              disabled={reuse.isPending || grid.headers.length === 0}
            >
              <CopyPlus className="size-4" />{" "}
              {reuse.isPending ? "Creating…" : "Create new visual from this data"}
            </Button>
            {dirty ? (
              <p className="text-xs text-muted-foreground">
                Unsaved changes here are copied across too.
              </p>
            ) : null}
          </div>

          <div className="panel p-4">
            <ConfigPane kind={kind} grid={grid} config={config} onChange={updateConfig} />
          </div>
        </aside>
      </div>
    </AppShell>
  );
}
